import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Shield, Users, FileText, CheckCircle2, Search, ArrowLeft, ChevronDown, ChevronRight,
  ExternalLink, Edit, Loader2, AlertTriangle, Clock, Instagram, MessageCircle, Save,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { adminOverview, getContactSettings, updateContactSettings } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 16);
    return d.toLocaleString("es-MX", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso).slice(0, 16); }
}

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    if (loading) return;
    if (!user?.is_admin) return; // Navigate handled below
    (async () => {
      try {
        setFetching(true);
        const d = await adminOverview();
        setData(d);
      } catch (e) {
        const msg = e?.response?.data?.detail || "No se pudo cargar el panel";
        setError(msg);
        toast.error(msg);
      } finally {
        setFetching(false);
      }
    })();
  }, [loading, user]);

  const filtered = useMemo(() => {
    if (!data?.users) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.users;
    return data.users.filter((u) => {
      const inUser = `${u.name} ${u.email}`.toLowerCase().includes(needle);
      const inInv = (u.invitations || []).some((i) =>
        `${i.title} ${i.slug} ${i.theme}`.toLowerCase().includes(needle)
      );
      return inUser || inInv;
    });
  }, [data, q]);

  const toggle = (uid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream">
        <Loader2 className="animate-spin text-warmgold" size={28} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream p-6" data-testid="admin-forbidden">
        <div className="text-center max-w-sm">
          <AlertTriangle className="mx-auto text-red-500" size={32} />
          <h1 className="headline text-2xl text-ink mt-4">Área restringida</h1>
          <p className="text-inksoft text-sm mt-2">Esta zona es solo para el administrador de Fernebia.</p>
          <button onClick={() => navigate("/dashboard")} className="btn-ghost mt-6">
            <ArrowLeft size={14} /> Volver al panel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="mx-auto max-w-7xl px-5 md:px-10 py-8">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => navigate("/dashboard")} className="btn-ghost" data-testid="admin-back-btn">
            <ArrowLeft size={16} /> Panel
          </button>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-warmgold/10 border border-warmgold/40 text-warmgold text-xs uppercase tracking-widest">
            <Shield size={14} /> Modo administrador
          </div>
        </div>

        <header className="mt-6">
          <p className="overline">Fernebia · consola de soporte</p>
          <h1 className="headline text-4xl md:text-5xl text-ink mt-2">Todos los anfitriones</h1>
          <p className="text-inksoft text-sm mt-2">Revisa cuentas, entra a sus invitaciones y ayúdales a corregir detalles.</p>
        </header>

        <ContactSettings />

        {data?.totals && (
          <div className="mt-6 grid grid-cols-3 gap-3 md:gap-6 max-w-2xl" data-testid="admin-totals">
            <TotalCard icon={<Users size={16} />} label="Usuarios" value={data.totals.users} />
            <TotalCard icon={<FileText size={16} />} label="Invitaciones" value={data.totals.invitations} />
            <TotalCard icon={<CheckCircle2 size={16} />} label="RSVPs" value={data.totals.rsvps} />
          </div>
        )}

        <div className="mt-8 relative max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-inksoft" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, correo o invitación…"
            className="w-full bg-white border border-warmgold/30 rounded-full pl-10 pr-4 py-2.5 text-sm text-ink focus:outline-none focus:border-warmgold"
            data-testid="admin-search"
          />
        </div>

        {fetching ? (
          <div className="mt-16 text-center text-inksoft" data-testid="admin-loading">
            <Loader2 className="mx-auto animate-spin text-warmgold" size={28} />
            <p className="mt-3 text-sm">Cargando datos…</p>
          </div>
        ) : error ? (
          <p className="mt-10 text-red-600 text-sm" data-testid="admin-error">{error}</p>
        ) : (
          <div className="mt-6 space-y-3">
            {filtered.length === 0 && (
              <p className="text-inksoft text-sm py-10 text-center" data-testid="admin-empty">Sin resultados.</p>
            )}
            {filtered.map((u) => (
              <UserRow
                key={u.user_id}
                user={u}
                open={expanded.has(u.user_id)}
                onToggle={() => toggle(u.user_id)}
              />
            ))}

            {data?.orphan_invitations?.length > 0 && (
              <div className="mt-10">
                <p className="overline">Invitaciones sin dueño</p>
                <div className="mt-3 space-y-2">
                  {data.orphan_invitations.map((i) => <InvitationRow key={i.id} inv={i} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ContactSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getContactSettings().then(setForm).catch(() => setForm({ instagram: "", whatsapp: "", whatsapp_message: "" }));
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      const saved = await updateContactSettings(form);
      setForm(saved);
      toast.success("Contacto actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="mt-6 rounded-2xl border border-warmgold/30 bg-white p-5" data-testid="contact-settings-loading">
        <Loader2 className="animate-spin text-warmgold" size={18} />
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-2xl border border-warmgold/30 bg-white p-5 md:p-6 max-w-2xl" data-testid="contact-settings">
      <div className="flex items-center gap-2">
        <MessageCircle size={16} className="text-warmgold" />
        <p className="overline">Contacto público · Instagram y WhatsApp</p>
      </div>
      <p className="text-inksoft text-sm mt-2">Estos datos alimentan los botones de la página de inicio con los que tus clientes te escriben.</p>

      <label className="block mt-5" data-testid="contact-instagram-field">
        <span className="text-xs tracking-widest uppercase text-inksoft flex items-center gap-1"><Instagram size={13} /> Instagram</span>
        <input
          value={form.instagram || ""}
          onChange={(e) => setForm({ ...form, instagram: e.target.value })}
          placeholder="@tu_usuario o https://instagram.com/tu_usuario"
          className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/50"
          data-testid="contact-instagram-input"
        />
      </label>

      <label className="block mt-5" data-testid="contact-whatsapp-field">
        <span className="text-xs tracking-widest uppercase text-inksoft flex items-center gap-1"><MessageCircle size={13} /> WhatsApp (con código de país)</span>
        <input
          value={form.whatsapp || ""}
          onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
          placeholder="Ej. 52 1 55 1234 5678"
          className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/50"
          data-testid="contact-whatsapp-input"
        />
        <span className="block text-[11px] text-inksoft mt-1">Incluye el código de país (México = 52). Solo se usan los dígitos.</span>
      </label>

      <label className="block mt-5" data-testid="contact-message-field">
        <span className="text-xs tracking-widest uppercase text-inksoft">Mensaje automático de WhatsApp</span>
        <textarea
          value={form.whatsapp_message || ""}
          onChange={(e) => setForm({ ...form, whatsapp_message: e.target.value })}
          rows={2}
          placeholder="Hola 👋 Me interesa una invitación digital…"
          className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/50 resize-none"
          data-testid="contact-message-input"
        />
      </label>

      <button onClick={save} disabled={saving} className="btn-primary mt-6 disabled:opacity-60" data-testid="contact-save-btn">
        {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Guardar contacto
      </button>
    </div>
  );
}

function TotalCard({ icon, label, value }) {  return (
    <div className="rounded-2xl px-4 py-3 border border-warmgold/25 bg-white">
      <p className="text-[10px] uppercase tracking-widest text-inksoft inline-flex items-center gap-1.5">{icon} {label}</p>
      <p className="mt-1 text-2xl font-semibold text-ink">{value}</p>
    </div>
  );
}

function UserRow({ user, open, onToggle }) {
  const inv = user.invitations || [];
  return (
    <div className="rounded-2xl border border-warmgold/25 bg-white overflow-hidden" data-testid={`admin-user-${user.user_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-warmgold/5 transition-colors"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-warmgold/15 grid place-items-center text-warmgold text-sm font-semibold">
            {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-ink font-medium truncate">{user.name || "(sin nombre)"}</p>
          <p className="text-inksoft text-xs truncate">{user.email}</p>
        </div>
        <div className="hidden md:flex flex-col items-end text-right text-xs text-inksoft shrink-0">
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {fmtDate(user.last_login) || "—"}</span>
          <span>{inv.length} invitación{inv.length === 1 ? "" : "es"}</span>
        </div>
        <span className="ml-2 shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-warmgold/10 text-warmgold">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-warmgold/15 bg-cream/60 px-4 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-inksoft mb-4">
            <MetaLine label="Registrado" value={fmtDate(user.created_at)} />
            <MetaLine label="Último ingreso" value={fmtDate(user.last_login)} />
            <MetaLine label="User ID" value={user.user_id?.slice(0, 12) + "…"} mono />
            <MetaLine label="Total invitaciones" value={inv.length} />
          </div>

          {inv.length === 0 ? (
            <p className="text-inksoft text-sm italic">Este usuario aún no ha creado invitaciones.</p>
          ) : (
            <div className="space-y-2">
              {inv.map((i) => <InvitationRow key={i.id} inv={i} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InvitationRow({ inv }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white border border-warmgold/20 px-3 py-2.5" data-testid={`admin-inv-${inv.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-ink font-medium truncate">{inv.title || "(sin título)"}</p>
          <span className="text-[10px] uppercase tracking-widest text-inksoft bg-warmgold/10 px-2 py-0.5 rounded-full">{inv.theme || "—"}</span>
          {!inv.is_active && (
            <span className="text-[10px] uppercase tracking-widest text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">Inactiva</span>
          )}
        </div>
        <p className="text-inksoft text-xs mt-0.5 truncate">
          /{inv.slug} · {inv.event_date || "sin fecha"} {inv.event_time || ""} · {inv.rsvp_yes || 0}/{inv.rsvp_total || 0} confirmados ({inv.rsvp_people || 0} personas)
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Link to={`/i/${inv.slug}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs py-1.5 px-2.5" title="Ver invitación">
          <ExternalLink size={13} />
        </Link>
        <Link to={`/editor/${inv.id}`} className="btn-primary text-xs py-1.5 px-2.5" title="Editar invitación">
          <Edit size={13} />
        </Link>
      </div>
    </div>
  );
}

function MetaLine({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-inksoft/70">{label}</p>
      <p className={`text-ink text-xs mt-0.5 truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</p>
    </div>
  );
}
